import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isPast } from 'date-fns';

export interface GroupRegistrationItem {
  id: string;
  event_id: string;
  status: string;
  payment_status: string;
  registered_at: string;
  cancelled_at: string | null;
  notes: string | null;
  price_at_registration: number | null;
  currency_at_registration: string | null;
  precise_address: string | null;
  event: {
    id: string;
    title: string;
    title_en: string | null;
    title_zh: string | null;
    description: string | null;
    description_en: string | null;
    description_zh: string | null;
    event_date: string;
    end_date: string | null;
    duration_minutes: number;
    location_type: string;
    location_details: string | null;
    price: number;
    currency: string;
    max_participants: number;
    cover_image_url: string | null;
    status: string;
    practitioner_id: string;
  };
  practitioner_name: string;
}

export const useGroupRegistrations = () => {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<GroupRegistrationItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRegistrations = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('group_event_registrations')
        .select(`
          id, event_id, status, payment_status, registered_at, cancelled_at, notes,
          price_at_registration, currency_at_registration,
          group_events!inner (
            id, title, title_en, title_zh, description, description_en, description_zh,
            event_date, end_date, duration_minutes, location_type, location_details,
            price, currency, max_participants, cover_image_url, status, practitioner_id,
            precise_address,
            practitioners!inner (first_name, last_name)
          )
        `)
        .eq('client_id', user.id)
        .order('registered_at', { ascending: false });

      if (error) throw error;

      const mapped: GroupRegistrationItem[] = (data || []).map((r: any) => {
        const ge = r.group_events;
        const prac = ge.practitioners;
        const practName = prac ? `${prac.first_name || ''} ${prac.last_name || ''}`.trim() || 'Unknown' : 'Unknown';
        return {
          id: r.id,
          event_id: r.event_id,
          status: r.status,
          payment_status: r.payment_status,
          registered_at: r.registered_at,
          cancelled_at: r.cancelled_at,
          notes: r.notes,
          price_at_registration: r.price_at_registration,
          currency_at_registration: r.currency_at_registration,
          precise_address: ['registered', 'attended'].includes(r.status) ? ge.precise_address : null,
          event: ge,
          practitioner_name: practName,
        };
      });

      setRegistrations(mapped);
    } catch (err) {
      console.error('Failed to fetch group registrations:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchRegistrations(); }, [fetchRegistrations]);

  const upcoming = registrations.filter(r =>
    !['cancelled'].includes(r.status) && !isPast(new Date(r.event.event_date))
  );
  const past = registrations.filter(r =>
    !['cancelled'].includes(r.status) && isPast(new Date(r.event.event_date))
  );

  return { upcoming, past, loading, refresh: fetchRegistrations };
};
